import { useEffect } from 'react'

const SITE_ORIGIN = 'https://10000-offline-books-in-10-min.altuzar.dev'

/** Resolve a relative path (e.g. `/app-screenshot.png`) to an absolute URL. */
function toAbsoluteUrl(value: string): string {
  if (/^https?:\/\//i.test(value)) return value
  return `${SITE_ORIGIN}${value.startsWith('/') ? '' : '/'}${value}`
}

interface SEOProps {
  title: string
  description: string
  image?: string
  url?: string
  type?: string
}

export function SEO({ title, description, image, url, type = 'website' }: SEOProps) {
  useEffect(() => {
    // Update document title
    document.title = title

    // Update meta tags. og:* uses the `property` attribute; twitter:* and the
    // standard SEO tags use `name` (per Twitter's spec).
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name'
      let element = document.querySelector(`meta[${attribute}="${name}"]`)

      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attribute, name)
        document.head.appendChild(element)
      }

      element.setAttribute('content', content)
    }

    updateMetaTag('description', description)
    updateMetaTag('og:title', title, true)
    updateMetaTag('og:description', description, true)
    updateMetaTag('og:type', type, true)
    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:title', title)
    updateMetaTag('twitter:description', description)

    if (image) {
      const absoluteImage = toAbsoluteUrl(image)
      updateMetaTag('og:image', absoluteImage, true)
      updateMetaTag('twitter:image', absoluteImage)
    }

    if (url) {
      updateMetaTag('og:url', url, true)
      updateMetaTag('twitter:url', url)

      // Update canonical URL
      let canonical = document.querySelector('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
      }
      canonical.setAttribute('href', url)
    }
  }, [title, description, image, url, type])

  return null
}
